import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { Shoot, ShootStatus } from '@studio/shared';

import { api, assetUrl } from '../lib/api';
import { useDialog } from '../components/Dialog';
import { ArrowRightIcon, PlusIcon, TrashIcon } from '../components/Icons';
import { Display } from '../components/Display';
import { useToast } from '../components/Toaster';
import { useDateFormat } from '../lib/dates';
import { useT } from '../lib/i18n';
import { useLoad } from '../lib/useLoad';

type Row = Shoot & {
  shotCount: number;
  doneCount: number;
  keeperCount: number;
  thumbnails: string[];
  running: boolean;
};

const STATUS_CLASS: Partial<Record<ShootStatus, string>> = {
  complete: 'badge--ok',
  failed: 'badge--bad',
  cancelled: 'badge--bad',
};

/**
 * The archive, as a list of rows on one grey slab: the date in large type,
 * the name, three of its frames fanned like prints, and where it stands — a
 * live count while it is being shot, the keepers once it is done. A name and
 * a count told you nothing about what was inside, so finding one meant
 * opening four.
 */
export function ShootsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { ask } = useDialog();
  const t = useT();
  const when = useDateFormat();
  // Polled: a shoot started on another device (or tab) should surface here too.
  const {
    data: shoots,
    loading,
    reload: refresh,
  } = useLoad(() => api.listShoots(), {
    initial: [] as Row[],
    every: 8000,
  });

  /*
   * What state a roll is in is the one thing you filter an archive by: you
   * came here either to watch something finish or to find something finished.
   * Offered only once there are enough rolls for the answer to be work —
   * three rows and a filter above them is a control that costs more than it
   * saves.
   */
  const [only, setOnly] = useState<'all' | 'running' | 'done'>('all');
  const rows = shoots.filter((shoot) =>
    only === 'running' ? shoot.running : only === 'done' ? !shoot.running : true,
  );
  const running = shoots.filter((shoot) => shoot.running).length;

  return (
    <div className="app__inner">
      <header className="pagehead">
        <Display text={t('rolls.display')} />
        <button className="btn btn--primary" onClick={() => navigate('/new')}>
          <PlusIcon /> {t('rolls.new')}
        </button>
      </header>

      {loading ? (
        <div className="stack stack--sm">
          {[0, 1].map((i) => (
            <div key={i} className="skeleton" style={{ height: 120, aspectRatio: 'auto' }} />
          ))}
        </div>
      ) : shoots.length === 0 ? (
        /* An empty archive is the one screen where there is nothing to look
           at, so it is the one that can afford to be a picture. */
        <section className="feature">
          <div className="feature__top">
            <span className="feature__chip">{t('rolls.eyebrow')}</span>
          </div>
          <div className="feature__text">
            <h2 className="feature__title">{t('rolls.empty')}</h2>
            <p className="feature__lede">{t('rolls.lede')}</p>
            <div style={{ marginTop: 14 }}>
              <button className="btn btn--primary" onClick={() => navigate('/new')}>
                <PlusIcon /> {t('rolls.emptyCta')}
              </button>
            </div>
          </div>
        </section>
      ) : (
        <>
          {shoots.length > 3 && (
            <div className="row row--wrap pagehead__filters">
              {(['all', 'running', 'done'] as const).map((id) => (
                <button
                  key={id}
                  type="button"
                  className={`chip ${only === id ? 'is-on' : ''}`}
                  onClick={() => setOnly(id)}
                >
                  {t(`rolls.only.${id}`)}
                  {id === 'running' && running > 0 && <b>{running}</b>}
                </button>
              ))}
            </div>
          )}

          <section
            className="srows"
            aria-label={rows.length === 1 ? t('rolls.countOne') : t('rolls.count', { n: rows.length })}
          >
            {rows.map((shoot, index) => {
              const created = new Date(shoot.createdAt);
              const day = String(created.getDate()).padStart(2, '0');
              const month = when.short(created).replace(/^\d+\s*/, '');
              return (
                <article key={shoot.id} className="srow" style={{ animationDelay: `${60 + index * 50}ms` }}>
                  {/* The date as the first thing read: an archive is looked
                      through by when, long before it is looked through by name. */}
                  <span className="srow__date" aria-hidden="true">
                    <b>{day}</b>
                    <small>{month}</small>
                  </span>

                  <span className="srow__text">
                    {/* The name is the link, and its hit area covers the row. */}
                    <Link to={`/shoots/${shoot.id}`} className="srow__name">
                      {shoot.name}
                    </Link>
                    <span className="srow__sub">
                      <span>{t(`rolls.mode.${shoot.settings.mode ?? 'presets'}`)}</span>
                      <span>
                        {shoot.povIds.length}{' '}
                        {t(shoot.povIds.length === 1 ? 'common.setUp' : 'common.setUps')}
                      </span>
                      <span>{when.short(shoot.createdAt)}</span>
                    </span>
                  </span>

                  {/* Three frames fanned like prints in a hand; they spread
                      apart when the row is pointed at. */}
                  <span className="srow__fan" aria-hidden="true">
                    {shoot.thumbnails.length ? (
                      shoot.thumbnails
                        .slice(0, 3)
                        .map((assetId) => <img key={assetId} src={assetUrl(assetId)} alt="" loading="lazy" />)
                    ) : (
                      <span className="srow__blank" />
                    )}
                  </span>

                  <span className="srow__state">
                    {shoot.running ? (
                      <>
                        <span className="livedot" aria-hidden="true" />
                        <b className="mono">
                          {shoot.doneCount} / {shoot.shotCount}
                        </b>
                        <span className="sr-only">{t('rolls.status.rolling')}</span>
                      </>
                    ) : shoot.status === 'complete' ? (
                      shoot.keeperCount > 0 ? (
                        <b>
                          {t(shoot.keeperCount === 1 ? 'rolls.keptOne' : 'rolls.kept', {
                            n: shoot.keeperCount,
                          })}
                        </b>
                      ) : (
                        <span>
                          {shoot.shotCount} {t(shoot.shotCount === 1 ? 'common.frame' : 'common.frames')}
                        </span>
                      )
                    ) : (
                      <span className={`badge ${STATUS_CLASS[shoot.status] ?? ''}`}>
                        {t(`enum.status.${shoot.status}`)}
                      </span>
                    )}
                  </span>

                  <span className="srow__end">
                    <button
                      type="button"
                      className="srow__delete"
                      aria-label={t('rolls.delete', { name: shoot.name })}
                      onClick={async () => {
                        /*
                         * A roll is every frame on it, each one paid for. It used
                         * to go on a single click with nothing asked.
                         */
                        const yes = await ask({
                          title: t('rolls.confirmDelete', { name: shoot.name }),
                          body: t('rolls.confirmDeleteBody', { n: shoot.shotCount }),
                          confirmLabel: t('common.deleteIt'),
                          danger: true,
                        });
                        if (!yes) return;
                        await api.deleteShoot(shoot.id);
                        toast(t('rolls.deleted', { name: shoot.name }));
                        refresh();
                      }}
                    >
                      <TrashIcon />
                    </button>
                    {/* The arrow opens the roll too: it sits above the row's own
                        link, so it has to be one itself or a press on it is lost. */}
                    <Link to={`/shoots/${shoot.id}`} className="srow__go" aria-hidden="true" tabIndex={-1}>
                      <ArrowRightIcon />
                    </Link>
                  </span>
                </article>
              );
            })}
          </section>
        </>
      )}
    </div>
  );
}
